import { z } from 'zod';

import { EntityId, IsoDate, Page, PageQuery, ToneName, Uid, Ulid, Via } from '../primitives';
import { MemberDTO, SessionDTO, SessionStatus } from '../sessions';
import { DateRangeQuery, TextFilter } from './common';

export const CmsSessionDTO = SessionDTO.extend({
  hostEmail: z.string().nullable(),
  deviceModel: z.string().nullable(),
  devicePlatform: z.string().nullable(),
  participantCount: z.number().int().nonnegative(),
  songsPlayed: z.number().int().nonnegative(),
});
export type CmsSessionDTO = z.infer<typeof CmsSessionDTO>;

/** `GET /cms/sessions?status=&from=&to=` */
export const CmsSessionsQuery = PageQuery.merge(DateRangeQuery).extend({
  status: SessionStatus.optional(),
  hostUid: Uid.optional(),
  deviceId: z.string().optional(),
  /** Join code, host email or host name. */
  q: TextFilter.optional(),
});
export type CmsSessionsQuery = z.infer<typeof CmsSessionsQuery>;
export type CmsSessionsQueryInput = z.input<typeof CmsSessionsQuery>;

/** Counters of the CMS-13 header ("37 activas ahora · 1.208 en los últimos 30 días"). */
export const SessionCounts = z.object({
  active: z.number().int().nonnegative(),
  last30d: z.number().int().nonnegative(),
});
export type SessionCounts = z.infer<typeof SessionCounts>;

export const CmsSessionsResponse = Page(CmsSessionDTO).extend({ counts: SessionCounts });
export type CmsSessionsResponse = z.infer<typeof CmsSessionsResponse>;

export const SessionSongStatus = z.enum(['queued', 'playing', 'played', 'skipped', 'removed']);
export type SessionSongStatus = z.infer<typeof SessionSongStatus>;

/** A `session_songs` history row (incl. removed). */
export const SessionSongDTO = z.object({
  id: Ulid,
  /** ULID or seed slug (`song_100010`) — the catalog uses both (docs/01 §6). */
  songId: EntityId,
  title: z.string(),
  artistNames: z.string(),
  tone: ToneName,
  addedByUserId: Uid,
  addedByName: z.string(),
  addedVia: Via,
  addedAt: IsoDate,
  startedAt: IsoDate.nullable(),
  playedAt: IsoDate.nullable(),
  status: SessionSongStatus,
  deletedAt: IsoDate.nullable(),
});
export type SessionSongDTO = z.infer<typeof SessionSongDTO>;

/**
 * A `session_participants` row as CMS-13 shows it: every participant, including the ones who
 * already left (`leftAt`), with the account behind the uid.
 */
export const CmsParticipantDTO = MemberDTO.extend({
  email: z.string().nullable(),
  isAnonymous: z.boolean(),
});
export type CmsParticipantDTO = z.infer<typeof CmsParticipantDTO>;

/** `GET /cms/sessions/:id` */
export const CmsSessionDetailResponse = z.object({
  session: CmsSessionDTO,
  participants: z.array(CmsParticipantDTO),
  history: z.array(SessionSongDTO),
});
export type CmsSessionDetailResponse = z.infer<typeof CmsSessionDetailResponse>;

/** `POST /cms/sessions/:id/close` */
export const CmsSessionCloseBody = z.object({ reason: z.string().max(500).optional() });
export type CmsSessionCloseBody = z.infer<typeof CmsSessionCloseBody>;

export const CmsSessionCloseResponse = z.object({ session: CmsSessionDTO });
export type CmsSessionCloseResponse = z.infer<typeof CmsSessionCloseResponse>;
